import { useMemo } from 'react';

import { useRouteStore } from '#hooks/useRouteStore.js';
import { useRouter } from '#router/useRouter.js';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom';

/**
 * Ensures there is a cause present for the route to render correctly,
 * if no cause is present in the provider, attempt to utilize one if provided via the search query params to
 * create a redirect to the stores/${storeId} route
 */
const StoreCauseGuard = () => {
  const { storeId } = useParams<{ storeId?: string; itemId?: string }>();
  const { useLocation } = useRouter();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const causeId = searchParams.get('causeId');

  const { getStoreId } = useRouteStore();

  // Remove the shop prefix from the pathname
  // Note- it would be preferable to use the basePath from the router, but it's not available without changing current routing logic
  const pathnameWithoutShop = useMemo(
    () => location.pathname.split('/shop')[1],
    [location.pathname]
  );

  // If a cause is present, or there is a getStoreId function as it's assumed the store is present
  if (storeId || getStoreId) {
    return <Outlet />;
  }

  // If no cause is present, attempt to create one via the causeId provided in the search params
  // Utilize the itemId to navigate to the correct item
  if (causeId) {
    return <Navigate to={`stores/${causeId}${pathnameWithoutShop ? pathnameWithoutShop : ''}`} />;
  }

  // If no cause can be assumed, redirect to the home page
  return <Navigate to="/home" replace />;
};

export { StoreCauseGuard };
